<template>
  <div class="ktb-result-container fail-page">
    <div class="navbar navbar-expand navbar-light bg-primary mb-4">
      <div class="container">
        <span class="navbar-brand text-white">
          <i class="fas fa-credit-card mr-2"></i>KTB Fast Pay
        </span>
      </div>
    </div>

    <div class="container py-4">
      <b-row class="justify-content-center">
        <b-col cols="12" md="8" lg="6">
          <b-card class="shadow-sm">
            <div class="text-center mb-4">
              <div class="fail-icon mb-3">
                <i class="fas fa-times-circle"></i>
              </div>
              <h2 class="mb-3">การชำระเงินไม่สำเร็จ</h2>
              <p class="lead text-muted">เกิดข้อผิดพลาดในการทำรายการ</p>
            </div>

            <!-- Loading state -->
            <div v-if="isLoading" class="text-center py-4 mb-4">
              <b-spinner variant="primary" label="กำลังโหลดข้อมูล"></b-spinner>
              <p class="mt-2">กำลังดึงข้อมูลการทำรายการ...</p>
            </div>

            <!-- Error details section -->
            <div v-if="errorCode || errorMessage" class="transaction-details p-3 border-danger mb-4">
              <div
                v-if="errorCode"
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <span class="text-muted">รหัสข้อผิดพลาด:</span>
                <span class="font-weight-bold">{{ errorCode }}</span>
              </div>
              <div
                v-if="errorMessage"
                class="d-flex justify-content-between align-items-center"
              >
                <span class="text-muted">ข้อความข้อผิดพลาด:</span>
                <span class="font-weight-bold">{{ errorMessage }}</span>
              </div>
            </div>

            <!-- Transaction details -->
            <div class="payment-summary p-3 bg-light rounded mb-4">
              <h5 class="font-weight-bold mb-3">รายละเอียดการทำรายการ</h5>
              <div class="d-flex justify-content-between mb-2">
                <span>จำนวนเงิน:</span>
                <strong>{{ formatCurrency(amount || getOrderAmount) }}</strong>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>วันที่ทำรายการ:</span>
                <strong>{{ formatDate(paymentDate || dateTime) }}</strong>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>สถานะ:</span>
                <strong class="text-danger">ไม่สำเร็จ</strong>
              </div>
              <div v-if="orderType" class="d-flex justify-content-between mb-2">
                <span>ประเภทรายการ:</span>
                <strong>{{ getOrderTypeText }}</strong>
              </div>
              <!-- Reference numbers -->
              <div v-if="dentCloudReferenceId" class="d-flex justify-content-between">
                <span>หมายเลขอ้างอิง DentCloud:</span>
                <strong>{{ dentCloudReferenceId }}</strong>
              </div>
            </div>


            <!-- Action buttons -->
            <div class="text-center">
              <b-button
                variant="outline-secondary"
                class="mr-2"
                @click="closeWindow"
              >
                ปิดหน้านี้
              </b-button>
              <b-button variant="primary" @click="tryAgain">
                <i class="fas fa-box mr-1"></i>ไปหน้าแพ็กเกจ
              </b-button>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

export default {
  name: "KtbFastPayFail",
  data() {
    return {
      amount: 0,
      dateTime: new Date(),
      errorCode: null,
      errorMessage: "",
      referenceId: null,
      transactionId: null,
      copyStatus: "",
      showCopySuccess: false,
      isLoading: false,
      apiErrorMessage: null,

      // Additional fields from API
      dentCloudReferenceId: null,
      ktbReferenceId: null,
      paymentDate: null,
      orderType: null,
    };
  },
  computed: {
    ...mapGetters({
      getBranchInfo: "moduleUser/getBranchInfo",
      transactionDetails: "modulePayment/getTransactionDetails",
      transactionDetailsLoading: "modulePayment/getTransactionDetailsLoading",
      transactionDetailsError: "modulePayment/getTransactionDetailsError",
    }),

    getOrderAmount() {
      if (this.transactionDetails && this.transactionDetails.order) {
        return this.transactionDetails.order.totalBahts;
      } else if (this.transactionDetails && this.transactionDetails.payment) {
        return this.transactionDetails.payment.amountBahts;
      }
      return 0;
    },

    getOrderTypeText() {
      if (!this.orderType) return '';

      const typeMap = {
        'storage_subscription': 'สมัครแพ็คเกจพื้นที่จัดเก็บ',
        'credit_topup': 'เติมเงินเครดิต',
        'subscription': 'สมัครแพ็คเกจบริการ',
      };

      return typeMap[this.orderType] || this.orderType;
    }
  },
  watch: {
    transactionDetailsLoading(newValue) {
      this.isLoading = newValue;
    },
    transactionDetailsError(newValue) {
      this.apiErrorMessage = newValue;
    },
    transactionDetails(newValue) {
      if (newValue) {
        this.updateTransactionDetails(newValue);
      }
    }
  },
  methods: {
    ...mapActions({
      fetchTransactionDetails: "modulePayment/fetchTransactionDetails",
    }),
    formatCurrency(value) {
      if (!value && value !== 0) return "฿0.00";
      return `฿${parseFloat(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).locale("th").format("DD MMM YYYY, HH:mm:ss");
    },
    closeWindow() {
      // Just try to close the window/tab and don't redirect
      try {
        window.close();
      } catch (e) {
        console.error("Error closing window:", e);
      }
    },
    tryAgain() {
      const { clinicUrl, branchUrl } = this.$route.params;
      if (clinicUrl && branchUrl) {
        this.$router.push({ name: "BillingPlans", params: { clinicUrl, branchUrl } });
      } else {
        this.$router.push("/clinic");
      }
    },
    copyToClipboard(text) {
      if (!text) return;

      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$bvToast.toast("คัดลอกไปยังคลิปบอร์ดแล้ว", {
            title: "สำเร็จ",
            variant: "success",
            solid: true,
            autoHideDelay: 2000,
          });
        })
        .catch(err => {
          console.error("Failed to copy: ", err);
          this.$bvToast.toast("ไม่สามารถคัดลอกได้", {
            title: "ข้อผิดพลาด",
            variant: "danger",
            solid: true,
            autoHideDelay: 2000,
          });
        });
    },

    parseQueryParams() {
      // Extract transaction information from URL query parameters
      const {
        amount,
        errorCode,
        errorMessage,
        dateTime,
        Ref, // KTB reference ID
        transactionId
      } = this.$route.query;

      if (amount) {
        this.amount = parseFloat(amount);
      }

      if (errorCode) {
        this.errorCode = errorCode;
      }

      if (errorMessage) {
        this.errorMessage = errorMessage;
      }

      if (transactionId) {
        this.transactionId = transactionId;
      }

      if (Ref) {
        this.referenceId = Ref;
      } else if (this.$route.query.ref) {
        this.referenceId = this.$route.query.ref;
      }

      if (dateTime) {
        this.dateTime = new Date(dateTime);
      } else {
        this.dateTime = new Date();
      }
    },

    async fetchTransactionDetailsData() {
      // Set loading state - hide any previous error
      this.apiErrorMessage = null;
      this.isLoading = true;

      const { clinicUrl, branchUrl } = this.$route.params;
      const referenceId = this.referenceId || this.$route.query.ref;

      if (!clinicUrl || !branchUrl || !referenceId) {
        // Silently fail
        this.isLoading = false;
        return;
      }

      try {
        await this.fetchTransactionDetails({
          clinicUrl,
          branchUrl,
          referenceId
        });
      } catch (error) {
        // Just log to console, but don't display error to user
        console.error("Error fetching transaction details:", error);
      } finally {
        this.isLoading = false;
      }
    },

    updateTransactionDetails(data) {
      if (!data) return;

      // Extract payment information
      if (data.payment) {
        this.dentCloudReferenceId = data.payment.dentCloudReferenceId;
        this.ktbReferenceId = data.payment.ktbFastPayReferenceId;
        this.paymentDate = data.payment.createdAt;
      }

      // Extract order information
      if (data.order) {
        this.orderType = data.order.type;
      }
    }
  },
  created() {
    this.parseQueryParams();
    this.fetchTransactionDetailsData();
  },
};
</script>

<style scoped>
.ktb-result-container {
  min-height: 100vh;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
}

.navbar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.container {
  flex-grow: 1;
}

.fail-icon {
  font-size: 5rem;
  color: #dc3545;
  animation: shake 0.8s ease-in-out;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20%,
  60% {
    transform: translateX(-10px);
  }
  40%,
  80% {
    transform: translateX(10px);
  }
}

.payment-summary {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.transaction-details {
  border: 2px solid #dc3545;
  border-radius: 8px;
  background-color: rgba(220, 53, 69, 0.05);
  transition: all 0.3s ease;
}

.transaction-details:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .container {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }

  .fail-icon {
    font-size: 4rem;
  }
}
</style>